/**
 * Payment-proof file access (COL-350, COL-436).
 *
 * Most payment proofs (tenant uploads, admin uploads) live in the `contracts`
 * bucket. Bank-transaction proofs written by finapi-sync-tick are different:
 * they carry the payer's IBAN/name/reference, so they live in the PRIVATE,
 * admin-only `bank-proofs` bucket. Both kinds are opened through a short-lived
 * signed URL: `contracts` is going private too (COL-436), and a public URL on
 * either bucket renders a blank page (the bug Neel hit).
 */
import { openStoredFile, resolveStoredFileUrl } from "@/lib/storage-url";

export const BANK_PROOF_PREFIX = "payment-proofs/bank-tx/";
const BANK_PROOF_BUCKET = "bank-proofs";
const PROOF_BUCKET = "contracts";
/** Long enough to view/download, short enough not to be a shareable link. */
const SIGNED_URL_TTL_SECONDS = 300;

export function isBankProofPath(path: string | null | undefined): boolean {
  return !!path && path.startsWith(BANK_PROOF_PREFIX);
}

export function proofBucket(path: string): string {
  return isBankProofPath(path) ? BANK_PROOF_BUCKET : PROOF_BUCKET;
}

/** Resolve a payment_proofs.file_path to a signed, openable URL. Returns null
 * when it can't (missing object, no read access). */
export async function resolveProofUrl(
  path: string | null | undefined
): Promise<string | null> {
  if (!path) return null;
  return resolveStoredFileUrl(path, proofBucket(path), {
    expiresIn: SIGNED_URL_TTL_SECONDS,
  });
}

/** Click-handler convenience: resolve and open in a new tab. Opens the tab
 * synchronously (popup-blocker safe) and navigates it once resolved.
 * Resolves false when the proof can't be opened, so the caller can say so. */
export async function openProofInNewTab(
  path: string | null | undefined
): Promise<boolean> {
  if (!path) return false;
  return openStoredFile(path, proofBucket(path), {
    expiresIn: SIGNED_URL_TTL_SECONDS,
  });
}
